import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Pencil, Trash2, X, Loader2, Briefcase, Cloud, Euro, FileSignature } from "lucide-react";
import { ModuloTemplateEditor } from "../components/modulo/ModuloTemplateEditor";
import { toast } from "sonner";
import CommessaNextcloudModal from "../components/CommessaNextcloudModal";
import CommessaEconomicoModal from "../components/CommessaEconomicoModal";
import { useAuth } from "../context/AuthContext";
import { canDoAction } from "../lib/menu";

const BLANK = {
  name: "", description: "", status: "attiva",
  modules: { cliente: true, sopralluogo: true, installazione: true, intervento: true },
  product_ids: [],
  email_sopralluogo: "", email_installazione: "", email_intervento: "", email_pratiche_allaccio: "", email_cc: "",
};
const EMAIL_FIELDS = [
  ["email_sopralluogo", "Sopralluogo", "Ricevono PDF e link a sopralluogo completato"],
  ["email_installazione", "Installazioni", "Ricevono pratica installazione, DiCo e link"],
  ["email_intervento", "Interventi", "Ricevono PDF e link a intervento completato"],
  ["email_pratiche_allaccio", "Pratiche allaccio in rete", "Indirizzi per le pratiche di allaccio (invio in arrivo)"],
  ["email_cc", "In copia (CC)", "Ricevono in copia tutte le email inviate a questa commessa"],
];
const toList = (t) => (t || "").split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

export default function Commesse() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduloFor, setModuloFor] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [ncCommessa, setNcCommessa] = useState(null);
  const [ecoCommessa, setEcoCommessa] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commesse");
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, description: c.description || "", status: c.status || "attiva",
      modules: c.modules || { cliente: true, sopralluogo: true, installazione: true, intervento: true },
      product_ids: c.product_ids || [],
      ...Object.fromEntries(EMAIL_FIELDS.map(([k]) => [k, (c[k] || []).join("\n")])),
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, ...Object.fromEntries(EMAIL_FIELDS.map(([k]) => [k, toList(form[k])])) };
      if (editing) await api.put(`/commesse/${editing.id}`, body);
      else await api.post("/commesse", body);
      toast.success(editing ? "Commessa aggiornata" : "Commessa creata");
      setShowForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la commessa ${c.name}?`)) return;
    try { await api.delete(`/commesse/${c.id}`); toast.success("Eliminata"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const modulesLabel = (m) => {
    const parts = [];
    if (m?.cliente) parts.push("Cliente");
    if (m?.sopralluogo) parts.push("Sopralluogo");
    if (m?.installazione) parts.push("Installazione");
    if (m?.intervento) parts.push("Interventi");
    return parts.join(" + ") || "—";
  };

  return (
    <div data-testid="commesse-page">
      {moduloFor && <ModuloTemplateEditor commessa={moduloFor} onClose={() => setModuloFor(null)} />}
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Commesse</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {list.length} commesse configurate
          </p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-commessa-btn">
          <Plus className="w-4 h-4" /> Nuova commessa
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : list.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <Briefcase className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="font-semibold">Nessuna commessa</div>
          <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>Crea la prima commessa per iniziare</div>
          <button onClick={openNew} className="btn-primary">Crea commessa</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <div key={c.id} className="card-shell p-5" data-testid={`commessa-card-${c.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-bold text-lg leading-tight" style={{ fontFamily: "Manrope" }}>{c.name}</div>
                  <span className={`badge badge-${c.status} mt-1 inline-block`}>{c.status}</span>
                </div>
                <div className="flex gap-1">
                  {user?.role === "admin" && canDoAction(user, "economico_view") && (
                    <button onClick={() => setEcoCommessa(c)} className="p-1.5 rounded hover:bg-black/5" title="Economico (listino guadagni)" data-testid={`economico-commessa-${c.id}`}>
                      <Euro className="w-4 h-4" style={{ color: "var(--primary)" }} />
                    </button>
                  )}
                  <button onClick={() => setNcCommessa(c)} className="p-1.5 rounded hover:bg-black/5" title="Configura Aruba Drive" data-testid={`nextcloud-commessa-${c.id}`}>
                    <Cloud className="w-4 h-4" style={{ color: "var(--primary)" }} />
                  </button>
                  <button onClick={() => setModuloFor(c)} className="p-1.5 rounded hover:bg-black/5" title="Modulo sopralluogo (PDF compilabile)" data-testid={`modulo-commessa-${c.id}`}><FileSignature className="w-4 h-4" /></button>
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-black/5" data-testid={`edit-commessa-${c.id}`}><Pencil className="w-4 h-4" /></button>
                  {canDoAction(user, "commesse_delete") && (
                    <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-black/5" data-testid={`delete-commessa-${c.id}`}><Trash2 className="w-4 h-4" style={{ color: "#B30000" }} /></button>
                  )}
                </div>
              </div>
              {c.description && <p className="text-sm my-2" style={{ color: "var(--text-muted)" }}>{c.description}</p>}
              {EMAIL_FIELDS.some(([k]) => (c[k] || []).length > 0) && (
                <div className="text-xs my-2 space-y-0.5" style={{ color: "var(--text-muted)" }} data-testid={`commessa-emails-${c.id}`}>
                  {EMAIL_FIELDS.filter(([k]) => (c[k] || []).length > 0).map(([k, label]) => <div key={k}><b>{label}:</b> {c[k].join(", ")}</div>)}
                </div>
              )}
              <div className="text-xs uppercase tracking-wider font-semibold mt-3" style={{ color: "var(--text-muted)" }}>Moduli attivi</div>
              <div className="text-sm font-medium mt-1">{modulesLabel(c.modules)}</div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowForm(false)}>
          <div className="card-shell w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="commessa-form-modal">
            <div className="p-5 border-b flex justify-between items-center" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{editing ? "Modifica commessa" : "Nuova commessa"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nome *</label>
                <input required data-testid="commessa-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Descrizione</label>
                <textarea rows={2} data-testid="commessa-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Stato</label>
                <select data-testid="commessa-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}>
                  <option value="attiva">Attiva</option>
                  <option value="sospeso">Sospesa</option>
                  <option value="completato">Completata</option>
                </select>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Moduli attivi nella scheda cliente</div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked disabled className="w-4 h-4 accent-[var(--primary)]" />
                    Dati cliente (sempre attivo)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="mod-sopralluogo" type="checkbox" checked={!!form.modules.sopralluogo} onChange={(e) => setForm({ ...form, modules: { ...form.modules, sopralluogo: e.target.checked } })} className="w-4 h-4 accent-[var(--primary)]" />
                    Sopralluogo
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="mod-installazione" type="checkbox" checked={!!form.modules.installazione} onChange={(e) => setForm({ ...form, modules: { ...form.modules, installazione: e.target.checked } })} className="w-4 h-4 accent-[var(--primary)]" />
                    Installazione
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="mod-intervento" type="checkbox" checked={!!form.modules.intervento} onChange={(e) => setForm({ ...form, modules: { ...form.modules, intervento: e.target.checked } })} className="w-4 h-4 accent-[var(--primary)]" />
                    Interventi
                  </label>
                </div>
              </div>
              <div className="space-y-3" data-testid="commessa-email-block">
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Email della commessa (una per riga)</div>
                {EMAIL_FIELDS.map(([k, label, help]) => (
                  <div key={k}>
                    <label className="text-sm font-semibold">{label}</label>
                    <textarea rows={2} data-testid={`commessa-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      placeholder={"ufficio@commessa.it\ntecnico@commessa.it"} className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none font-mono" style={{ borderColor: "var(--border)", background: "#fff" }} />
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{help}</div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="commessa-submit">{editing ? "Aggiorna" : "Crea"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {ncCommessa && (
        <CommessaNextcloudModal commessa={ncCommessa} onClose={() => setNcCommessa(null)} />
      )}

      {ecoCommessa && (
        <CommessaEconomicoModal commessa={ecoCommessa} onClose={() => setEcoCommessa(null)} />
      )}
    </div>
  );
}
